import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackApi, errMsg } from '../../lib/trackApi';
import DynamicField from './DynamicField';
import MemberPicker from './MemberPicker';
import { StatusSelect } from './StatusBadge';
import TaskCreateForm from './TaskCreateForm';
import {
  C, btn, btnGhost, btnDanger, btnSm, btnLink, input as inputStyle, labelStyle,
  fmtMD, fmtDateTime, fmtSize, isImage, statusOf, dueInfo, todayYmd,
} from './trackUi';

const DATE_TYPES = new Set(['due_change', 'start_change', 'mute']);

function logText(l) {
  const d = (v) => (v ? fmtMD(v) : '未設定');
  switch (l.type) {
    case 'created': return `建立任務（${l.newValue || ''}）`;
    case 'note': return '備註';
    case 'status': return `狀態 ${l.oldValue || ''} → ${l.newValue || ''}`;
    case 'due_change': return l.oldValue ? `預計完成 ${d(l.oldValue)} → ${d(l.newValue)}` : `預計完成 ${d(l.newValue)}`;
    case 'start_change': return `開始日 ${d(l.oldValue)} → ${d(l.newValue)}`;
    case 'mute': return l.newValue ? `暫停通知至 ${d(l.newValue)}` : '恢復通知';
    case 'assignee_change': return `執行人 ${l.oldValue || '未指派'} → ${l.newValue || '未指派'}`;
    case 'initiator_change': return `發起人 ${l.oldValue || '—'} → ${l.newValue || '—'}`;
    case 'field_change': return `${l.oldValue || ''} → ${l.newValue || '（空）'}`;
    case 'star': return l.newValue || '星號';
    case 'attachment': return `上傳 ${l.newValue || ''}`;
    case 'attachment_removed': return `刪除附件 ${l.oldValue || ''}`;
    case 'subtask': return `新增子任務 ${l.newValue || ''}`;
    case 'deleted': return '刪除任務';
    default: return DATE_TYPES.has(l.type) ? `${d(l.oldValue)} → ${d(l.newValue)}` : l.type;
  }
}

function Field({ label, children, wide }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function draftFrom(task) {
  return {
    title: task.title,
    content: task.content || '',
    initiatorId: task.initiatorId,
    assigneeId: task.assigneeId,
    status: task.status,
    startDate: task.startDate || '',
    dueDate: task.dueDate || '',
    muteUntil: task.muteUntil || '',
    starNote: task.starNote || '',
    extra: { ...(task.extra || {}) },
  };
}

export default function TaskDetailDrawer({ taskId, boards, members, onClose, onOpenTask, onChanged }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const [showDueHistory, setShowDueHistory] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const today = todayYmd();

  const load = useCallback(async () => {
    try {
      const d = await trackApi.getTask(taskId);
      if (!d?.ok) throw new Error(d?.error || '載入失敗');
      setData(d.data);
      setDraft(draftFrom(d.data.task));
      setError('');
    } catch (e) {
      setError(errMsg(e));
    }
  }, [taskId]);

  useEffect(() => {
    setData(null);
    setShowSub(false);
    setShowDueHistory(false);
    setReason('');
    setNote('');
    setMsg('');
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const task = data?.task;
  const board = useMemo(() => boards.find((b) => b.id === task?.boardId), [boards, task]);

  const patch = useMemo(() => {
    if (!task || !draft) return {};
    const p = {};
    const cmp = (k, a, b) => { if ((a ?? '') !== (b ?? '')) p[k] = a === '' ? null : a; };
    cmp('title', draft.title.trim(), task.title);
    cmp('content', draft.content, task.content || '');
    cmp('initiatorId', draft.initiatorId, task.initiatorId);
    cmp('assigneeId', draft.assigneeId, task.assigneeId);
    cmp('status', draft.status, task.status);
    cmp('startDate', draft.startDate, task.startDate || '');
    cmp('dueDate', draft.dueDate, task.dueDate || '');
    cmp('muteUntil', draft.muteUntil, task.muteUntil || '');
    cmp('starNote', draft.starNote, task.starNote || '');
    if (JSON.stringify(draft.extra || {}) !== JSON.stringify(task.extra || {})) p.extra = draft.extra;
    return p;
  }, [task, draft]);
  const dirty = Object.keys(patch).length > 0;
  const needReason = patch.dueDate !== undefined || patch.status !== undefined;

  const run = async (fn, okMsg) => {
    setBusy(true);
    setError('');
    setMsg('');
    try {
      await fn();
      await load();
      onChanged?.();
      if (okMsg) setMsg(okMsg);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(async () => {
    const body = { ...patch };
    if (reason.trim() && needReason) body.reason = reason.trim();
    const d = await trackApi.patchTask(task.id, body);
    if (!d?.ok) throw new Error(d?.error || '儲存失敗');
    setReason('');
  }, '已儲存');

  const toggleStar = () => run(() => trackApi.patchTask(task.id, { isStarred: !task.isStarred }));

  const sendNote = () => {
    if (!note.trim()) return;
    run(async () => {
      await trackApi.addNote(task.id, note.trim());
      setNote('');
    }, '備註已新增');
  };

  const upload = (files) => {
    if (!files?.length) return;
    run(() => trackApi.uploadAttachments(task.id, files), '已上傳');
  };

  const removeTask = () => {
    const extra = task.childCount ? `（含 ${task.childCount} 個子任務）` : '';
    if (!window.confirm(`刪除 ${task.taskNo}${extra}？`)) return;
    run(async () => {
      await trackApi.deleteTask(task.id);
      onClose();
    });
  };

  const set = (p) => setDraft((d) => ({ ...d, ...p }));

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,.3)' }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(680px, 100vw)',
          background: '#fff', boxShadow: '-6px 0 24px rgba(0,0,0,.15)', overflowY: 'auto', padding: '14px 18px 40px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <strong style={{ fontSize: 15 }}>{task?.taskNo || '任務'}</strong>
          {task && <span style={{ fontSize: 12, color: C.muted }}>{task.boardName}</span>}
          {task?.parentId && (
            <button type="button" style={btnLink} onClick={() => onOpenTask(task.parentId)}>
              屬 {task.parentNo} {task.parentTitle}
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {task && (
              <button
                type="button"
                title={task.isStarred ? '取消星號' : '加星號'}
                onClick={toggleStar}
                style={{ ...btnGhost, ...btnSm, color: task.isStarred ? C.star : '#9ca3af', fontSize: 16 }}
              >
                {task.isStarred ? '★' : '☆'}
              </button>
            )}
            {task && <button type="button" style={{ ...btnDanger, ...btnSm }} onClick={removeTask}>刪除</button>}
            <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={onClose}>關閉</button>
          </div>
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>}
        {msg && <div style={{ color: C.ok, fontSize: 13, marginBottom: 8 }}>{msg}</div>}
        {!task || !draft ? (
          <div style={{ color: C.muted }}>{error ? '' : '載入中…'}</div>
        ) : (
          <>
            <input
              style={{ ...inputStyle, width: '100%', fontSize: 17, fontWeight: 700, marginBottom: 12 }}
              value={draft.title}
              onChange={(e) => set({ title: e.target.value })}
            />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
              <Field label="狀態">
                <StatusSelect statuses={board?.statuses} value={draft.status} onChange={(v) => set({ status: v })} style={{ width: '100%' }} />
              </Field>
              <Field label="發起人">
                <MemberPicker members={members} value={draft.initiatorId} onChange={(v) => set({ initiatorId: v })} style={{ width: '100%' }} />
              </Field>
              <Field label="執行人">
                <MemberPicker members={members} value={draft.assigneeId} onChange={(v) => set({ assigneeId: v })} style={{ width: '100%' }} />
              </Field>
              <Field label="開始時間">
                <input type="date" style={{ ...inputStyle, width: '100%' }} value={draft.startDate} onChange={(e) => set({ startDate: e.target.value })} />
              </Field>
              <Field label={(
                <span>
                  預計完成時間
                  {data.dueHistory.length > 0 && (
                    <button type="button" style={{ ...btnLink, marginLeft: 6 }} onClick={() => setShowDueHistory((v) => !v)}>
                      歷史 {data.dueHistory.length}
                    </button>
                  )}
                </span>
              )}
              >
                <input
                  type="date"
                  style={{ ...inputStyle, width: '100%', color: dueInfo(task, today, board).color }}
                  value={draft.dueDate}
                  onChange={(e) => set({ dueDate: e.target.value })}
                />
              </Field>
              <Field label="暫停通知至（含）">
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="date" style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={draft.muteUntil} onChange={(e) => set({ muteUntil: e.target.value })} />
                  {draft.muteUntil && <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => set({ muteUntil: '' })}>清除</button>}
                </div>
              </Field>
            </div>

            {showDueHistory && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: 8, marginBottom: 10, fontSize: 13 }}>
                {data.dueHistory.map((h) => (
                  <div key={h.id} style={{ padding: '2px 0' }}>
                    <span style={{ color: C.muted, marginRight: 8 }}>{fmtDateTime(h.createdAt)}</span>
                    {h.oldValue ? `${fmtMD(h.oldValue)} → ` : ''}<strong>{h.newValue ? fmtMD(h.newValue) : '未設定'}</strong>
                    {h.actorLabel && <span style={{ color: C.muted }}>（{h.actorLabel}{h.source === 'line' ? '・LINE' : ''}）</span>}
                    {h.note && <span>：{h.note}</span>}
                  </div>
                ))}
              </div>
            )}

            {task.isStarred && (
              <Field label="星號備註" wide>
                <input style={{ ...inputStyle, width: '100%', marginBottom: 10 }} value={draft.starNote} onChange={(e) => set({ starNote: e.target.value })} />
              </Field>
            )}

            <Field label="內容" wide>
              <textarea rows={3} style={{ ...inputStyle, width: '100%', marginBottom: 10 }} value={draft.content} onChange={(e) => set({ content: e.target.value })} />
            </Field>

            {data.fields.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 10 }}>
                {data.fields.map((f) => (
                  <Field key={f.id} label={`${f.label}${f.isRequired ? ' *' : ''}`} wide={f.fieldType === 'textarea'}>
                    <DynamicField
                      field={f}
                      value={draft.extra[f.key]}
                      members={members}
                      onChange={(v) => set({ extra: { ...draft.extra, [f.key]: v } })}
                    />
                  </Field>
                ))}
              </div>
            )}

            {dirty && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                {needReason && (
                  <input
                    style={{ ...inputStyle, flex: 1, minWidth: 180 }}
                    placeholder="變更原因（選填）"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                )}
                <button type="button" style={btn} disabled={busy} onClick={save}>儲存變更</button>
                <button type="button" style={btnGhost} onClick={() => setDraft(draftFrom(task))}>還原</button>
              </div>
            )}

            <Section title="新增備註">
              <div style={{ display: 'flex', gap: 8 }}>
                <textarea
                  rows={2}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="進度、狀況…（自動記錄時間）"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendNote(); }}
                />
                <button type="button" style={btn} disabled={busy || !note.trim()} onClick={sendNote}>送出</button>
              </div>
            </Section>

            <Section title={`子任務（${data.children.filter((c) => statusOf(board, c.status).isDone).length}/${data.children.length}）`}>
              {data.children.map((c) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                  <span style={{ color: C.muted, width: 56 }}>{c.taskNo}</span>
                  <button type="button" style={{ ...btnLink, fontSize: 13, color: '#1f2937', textAlign: 'left', flex: 1 }} onClick={() => onOpenTask(c.id)}>
                    {c.isStarred ? '★ ' : ''}{c.title}
                  </button>
                  <span style={{ color: C.muted, width: 64 }}>{c.assigneeName || '未指派'}</span>
                  <span style={{ width: 90, color: dueInfo(c, today, board).color }}>{dueInfo(c, today, board).text}</span>
                  <StatusSelect
                    statuses={board?.statuses}
                    value={c.status}
                    onChange={(v) => run(() => trackApi.patchTask(c.id, { status: v }))}
                  />
                </div>
              ))}
              {showSub ? (
                <div style={{ marginTop: 8 }}>
                  <TaskCreateForm
                    boards={boards}
                    members={members}
                    parent={task}
                    onCreated={() => { setShowSub(false); load(); onChanged?.(); }}
                    onCancel={() => setShowSub(false)}
                  />
                </div>
              ) : (
                <button type="button" style={{ ...btnGhost, ...btnSm, marginTop: 6 }} onClick={() => setShowSub(true)}>＋ 子任務</button>
              )}
            </Section>

            <Section title={`附件（${data.attachments.length}）`}>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? C.brand : '#d1d5db'}`, borderRadius: 8, padding: 12, textAlign: 'center',
                  color: C.muted, fontSize: 13, cursor: 'pointer', background: dragOver ? C.brandSoft : '#fafafa', marginBottom: 8,
                }}
              >
                拖曳檔案到這裡或點擊上傳（單檔 20MB）
                <input ref={fileRef} type="file" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {data.attachments.map((a) => (
                  <div key={a.id} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 6, width: 140, fontSize: 12 }}>
                    <a href={a.url} target="_blank" rel="noreferrer" style={{ color: '#1f2937', textDecoration: 'none' }}>
                      {isImage(a) ? (
                        <img src={a.url} alt="" style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                      ) : (
                        <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', borderRadius: 4, fontWeight: 700, color: C.muted }}>
                          {(a.fileName.split('.').pop() || 'FILE').toUpperCase()}
                        </div>
                      )}
                      <div style={{ marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.fileName}>{a.fileName}</div>
                    </a>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: C.muted, marginTop: 2 }}>
                      <span>{fmtSize(a.size)}</span>
                      <button
                        type="button"
                        style={{ ...btnLink, color: C.danger }}
                        onClick={() => { if (window.confirm(`刪除 ${a.fileName}？`)) run(() => trackApi.deleteAttachment(a.id)); }}
                      >
                        刪除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="紀錄">
              {data.logs.map((l) => (
                <div key={l.id} style={{ padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                  <div style={{ display: 'flex', gap: 8, color: C.muted, fontSize: 12 }}>
                    <span>{fmtDateTime(l.createdAt)}</span>
                    <span>{l.actorLabel || '系統'}</span>
                    {l.source === 'line' && <span style={{ color: '#06c755', fontWeight: 700 }}>LINE</span>}
                  </div>
                  <div style={{ fontWeight: l.type === 'note' ? 400 : 600 }}>
                    {l.type === 'note' ? <span style={{ whiteSpace: 'pre-wrap' }}>{l.note}</span> : logText(l)}
                  </div>
                  {l.type !== 'note' && l.note && <div style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>原因：{l.note}</div>}
                </div>
              ))}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, borderBottom: `2px solid ${C.border}`, paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}
